import React, { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import PropTypes from 'prop-types';
import { FaCaretDown, FaCheck } from 'react-icons/fa';
import cn from 'classnames';

function FormSelect({
  options,
  name,
  label,
  placeHolder,
  onChange,
  value,
  error,
  required,
  disabled,
}) {
  const handleChange = (newValue) => {
    onChange(name, newValue);
  };
  const isError = !!error;
  return (
    <div className="relative w-full text-gray-600">
      <Listbox value={value} onChange={handleChange}>
        <div
          className={cn(
            'border rounded-md focus-within:ring-1 z-[1]',
            isError
              ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500 animate-wiggle'
              : 'border-gray-200 focus-within:ring-primary-600 focus-within:border-primary-600',
            disabled ? 'bg-gray-100' : 'bg-white',
          )}
        >
          <Listbox.Button className="w-full px-3 py-2">
            <span
              className={cn(
                value ? 'text-base' : 'text-base px-2 text-gray-500',
                'block truncate text-left',
              )}
            >
              {value
                ? options.find((x) => x.value === value)?.label
                : placeHolder}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <FaCaretDown
                className="w-5 h-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Listbox.Label
            className={cn(
              'absolute top-0 left-0 transform translate-x-[12px] -translate-y-[8px] bg-white px-2 text-[10px] peer-focus:text-primary',
              value ? 'text-primary' : 'text-gray-300',
            )}
          >
            <span>{label}</span>
            {required && (
              <span className="ml-1 font-light text-red-500">*</span>
            )}
          </Listbox.Label>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="z-10 absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {options.map((item) => (
                <Listbox.Option
                  key={item?.value}
                  className={({ active }) =>
                    `cursor-default select-none relative py-2 pl-10 pr-4 ${
                      active
                        ? 'text-secondary-900 bg-secondary-100'
                        : 'text-gray-900'
                    }`
                  }
                  value={item?.value}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {item?.label}
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-secondary-600">
                          <FaCheck className="w-5 h-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {error ? (
        <span className="absolute right-2 top-1 text-red-500 text-[10px] pl-2 bg-white">
          {error}
        </span>
      ) : null}
    </div>
  );
}

FormSelect.defaultProps = {
  placeHolder: 'Select an option',
  error: '',
  required: false,
  disabled: false,
};

FormSelect.propTypes = {
  options: PropTypes.instanceOf(Array).isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeHolder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default FormSelect;
